import {
  executeStepLoader,
  executeStepFunction,
  hasStepLoader,
  createLoadedDataSummary,
} from "../../utils/command/stepDataLoader";

/**
 * StepProcessor - Handles individual step processing logic
 */
export class StepProcessor {
  constructor(commandProcessor, promptResolver) {
    this.commandProcessor = commandProcessor;
    this.promptResolver = promptResolver;
  }

  /**
   * Process a step that has a prompt (regular step)
   */
  async processPromptStep(step, sessionData, introMessage = "") {
    // If step is undefined, return error
    if (!step) {
      return { error: "No step available for processing." };
    }

    // Extract callbacks from session data
    const callbacks = sessionData.$callbacks || {};

    // Execute load function if present
    let loadedData = null;
    if (hasStepLoader(step)) {
      try {
        loadedData = await executeStepLoader(step, sessionData, callbacks);
        console.log(
          "🔍 Step data loaded:",
          createLoadedDataSummary(loadedData)
        );

        // Merge loaded data into sessionData to persist for future steps
        if (loadedData && typeof loadedData === "object") {
          Object.assign(sessionData, loadedData);
          console.log("🔄 Loaded data merged into sessionData for persistence");
        }
      } catch (error) {
        return { error: `Load failed: ${error.message}` };
      }
    }

    const prompt = await this.promptResolver.resolvePrompt(step, {
      sessionData,
      loadedData,
      callbacks,
    });

    if (prompt) {
      const fullPrompt = this.promptResolver.combinePrompts(
        introMessage,
        prompt
      );
      return { prompt: fullPrompt, loadedData }; // Include loaded data in result
    } else {
      return { error: "Failed to resolve prompt." };
    }
  }

  /**
   * Process a step without a prompt (auto-advance step)
   */
  async processAutoAdvanceStep(
    step,
    sessionData,
    introMessage = "",
    callbacks = {}
  ) {
    return await this.processStep(
      step,
      null,
      sessionData,
      introMessage,
      callbacks
    );
  }

  /**
   * Process a step without a prompt (auto-advance step)
   */
  async processStep(
    step,
    userInput,
    sessionData,
    introMessage = "",
    callbacks = {}
  ) {
    try {
      // Execute load function if present
      let loadedData = null;
      if (hasStepLoader(step)) {
        try {
          loadedData = await executeStepLoader(step, sessionData, callbacks);
          console.log(
            "🔍 Step data loaded:",
            createLoadedDataSummary(loadedData)
          );

          // Merge loaded data into sessionData to persist for future steps
          if (loadedData && typeof loadedData === "object") {
            Object.assign(sessionData, loadedData);
            console.log(
              "🔄 Loaded data merged into sessionData for persistence"
            );
          }
        } catch (error) {
          return { error: `Load failed: ${error.message}` };
        }
      }

      let combinedOutput = introMessage;

      // Process step intro
      const stepIntro = await this._processStepIntro(
        step,
        sessionData,
        callbacks,
        loadedData
      );
      if (stepIntro) {
        if (combinedOutput) combinedOutput += "\n";
        combinedOutput += stepIntro;
      }

      // Process validation
      if (step.validate) {
        const validationResult = await this.commandProcessor.validateInput(
          step,
          userInput,
          sessionData,
          loadedData,
          callbacks
        );
        if (validationResult !== true) {
          return { error: validationResult };
        }
      }

      // Update session data
      if (step.onStep) {
        const dynamicData = await executeStepFunction(
          step.onStep,
          userInput,
          sessionData,
          loadedData,
          callbacks
        );
        Object.assign(sessionData, dynamicData);
      }

      // Process step outro
      const stepOutro = await this.commandProcessor.processStepOutro(
        step,
        userInput,
        sessionData,
        loadedData,
        callbacks
      );
      if (stepOutro) {
        if (combinedOutput) combinedOutput += "\n";
        combinedOutput += stepOutro;
      }

      return { output: combinedOutput || null };
    } catch (error) {
      console.error("Error processing step:", error);
      return { error: `Step processing error: ${error.message}` };
    }
  }

  /**
   * Process step intro if it exists
   */
  async _processStepIntro(step, sessionData, callbacks, loadedData) {
    if (!step.intro) return null;

    return executeStepFunction(
      step.intro,
      null,
      sessionData,
      loadedData,
      callbacks
    );
  }
}
